/**
 * LaTeXText Component
 *
 * Renders text with LaTeX expressions properly formatted.
 * - Web: Uses KaTeX for rendering math expressions
 * - Mobile: Falls back to plain text
 */

import React from 'react';
import {
    Text,
    Platform,
    StyleProp,
    TextStyle,
    Linking,
    StyleSheet,
} from 'react-native';
import { parseLatex, containsLatex } from '../utils/latexParser';
import colors from '../lib/colors';

// Conditional import for web only
let TeX: any = null;
if (Platform.OS === 'web') {
    try {
        // @ts-ignore - dynamic import for web only
        TeX = require('@matejmazur/react-katex').default;
    } catch (error) {
        console.warn('Failed to load react-katex:', error);
    }
}

interface LaTeXTextProps {
    text: string;
    style?: StyleProp<TextStyle>;
    fontsLoaded?: boolean;
    numberOfLines?: number;
    linkifyUrls?: boolean;
}

const URL_REGEX = /((?:https?:\/\/|www\.)[^\s<]+)/g;
const TRAILING_PUNCTUATION_REGEX = /[.,!?;:)\]]+$/;

function normalizeUrl(url: string): string {
    if (url.startsWith('www.')) {
        return `https://${url}`;
    }

    return url;
}

function splitTrailingPunctuation(rawUrl: string): { url: string; trailing: string } {
    const trailingMatch = rawUrl.match(TRAILING_PUNCTUATION_REGEX);

    if (!trailingMatch) {
        return { url: rawUrl, trailing: '' };
    }

    const trailing = trailingMatch[0];
    return {
        url: rawUrl.slice(0, rawUrl.length - trailing.length),
        trailing,
    };
}

export default function LaTeXText({
    text,
    style,
    fontsLoaded = false,
    numberOfLines,
    linkifyUrls = false,
}: LaTeXTextProps) {
    const openUrl = async (href: string) => {
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
            window.open(href, '_blank', 'noopener,noreferrer');
            return;
        }

        await Linking.openURL(href);
    };

    const renderTextContent = (content: string, keyPrefix: string) => {
        if (!linkifyUrls) {
            return <React.Fragment key={keyPrefix}>{content}</React.Fragment>;
        }

        const nodes: React.ReactNode[] = [];
        let lastIndex = 0;
        let matchIndex = 0;

        for (const match of content.matchAll(URL_REGEX)) {
            const rawMatch = match[0];
            const startIndex = match.index ?? 0;

            if (startIndex > lastIndex) {
                nodes.push(
                    <React.Fragment key={`${keyPrefix}-text-${matchIndex}`}>
                        {content.slice(lastIndex, startIndex)}
                    </React.Fragment>
                );
            }

            const { url, trailing } = splitTrailingPunctuation(rawMatch);
            const normalizedUrl = normalizeUrl(url);

            if (url) {
                nodes.push(
                    <Text
                        key={`${keyPrefix}-link-${matchIndex}`}
                        style={styles.linkText}
                        accessibilityRole="link"
                        onPress={() => {
                            void openUrl(normalizedUrl);
                        }}
                    >
                        {url}
                    </Text>
                );
            }

            if (trailing) {
                nodes.push(
                    <React.Fragment key={`${keyPrefix}-trailing-${matchIndex}`}>
                        {trailing}
                    </React.Fragment>
                );
            }

            lastIndex = startIndex + rawMatch.length;
            matchIndex += 1;
        }

        if (lastIndex < content.length) {
            nodes.push(
                <React.Fragment key={`${keyPrefix}-tail`}>
                    {content.slice(lastIndex)}
                </React.Fragment>
            );
        }

        if (nodes.length === 0) {
            return <React.Fragment key={keyPrefix}>{content}</React.Fragment>;
        }

        return nodes;
    };

    // If not on web or KaTeX failed to load, render plain text
    if (Platform.OS !== 'web' || !TeX) {
        return (
            <Text style={style} numberOfLines={numberOfLines}>
                {renderTextContent(text, 'plain')}
            </Text>
        );
    }

    // If no LaTeX expressions found, render plain text
    if (!containsLatex(text)) {
        return (
            <Text style={style} numberOfLines={numberOfLines}>
                {renderTextContent(text, 'plain')}
            </Text>
        );
    }

    // Parse text into segments
    const segments = parseLatex(text);

    // Render segments with LaTeX
    return (
        <Text style={style} numberOfLines={numberOfLines}>
            {segments.map((segment) => {
                if (segment.type === 'text') {
                    return renderTextContent(segment.content, segment.key);
                }

                // Render LaTeX expression
                try {
                    if (segment.type === 'inline-math') {
                        return (
                            <TeX key={segment.key} math={segment.content} />
                        );
                    } else {
                        // Display math
                        return (
                            <TeX key={segment.key} math={segment.content} block />
                        );
                    }
                } catch (error) {
                    // If LaTeX rendering fails, fall back to plain text
                    console.warn('LaTeX rendering error:', error);
                    return (
                        <React.Fragment key={segment.key}>
                            {segment.type === 'inline-math' ? `$${segment.content}$` : `$$${segment.content}$$`}
                        </React.Fragment>
                    );
                }
            })}
        </Text>
    );
}

const styles = StyleSheet.create({
    linkText: {
        color: colors.primaryGreenDark,
        textDecorationLine: 'underline',
    },
});
